import React, { useContext, useState } from "react";
import { TableBody, TableHead, TableRow, Chip, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { api } from "../../../../shared/hooks/api";
import { PendingProductImport } from "../../../../data/entities";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import { StyledTableCell, StyledTable } from "../../../../shared/components/CustomTable/Styled";
import { ContentWrapper } from "../../../../shared/styled/TableStyles";
import { error, success } from "../../../../utils/toast";
import { formatNumber } from "../../../../utils/format";
import BulkImportModal from "./BulkImportModal";
import LoadingButton from "../../../../shared/components/LoadingButton";

const IMPORT_ROLES = [
    ROLE.MANAGER,
    ROLE.FACTORY_MANAGER_ONE,
    ROLE.FACTORY_MANAGER_TWO,
];

const TABLE_COLUMNS = [
    { text: "Tên sản phẩm" },
    { text: "Mã sản phẩm" },
    { text: "Đơn hàng" },
    { text: "Tổng SL đề nghị nhập" },
    { text: "Hành động" },
];

const MAX_TAGS_SHOWN = 2;

const defaultFormState = () => ({
    receiver_name: "",
    import_date: dayjs(),
    import_method: "đề nghị nhập kho sản xuất",
    note: "",
});

const PendingImportContent = () => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canImport = IMPORT_ROLES.includes(role as ROLE);

    const [{ data, isLoading }, refetch] = api.get<PendingProductImport[]>(
        "/product-flow/v2/pending-product-imports/"
    );

    const [{ isCreating }, createBulkImport] = api.post(
        "/product-flow/v2/bulk-request-import/"
    );

    const [searchText, setSearchText] = useState("");
    const [selectedItem, setSelectedItem] = useState<PendingProductImport | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState(defaultFormState());

    const handleOpenModal = (item: PendingProductImport) => {
        if (isCreating) return;
        setSelectedItem(item);
        setFormState(defaultFormState());
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
    };

    const handleFormChange = (changes: Partial<typeof formState>) => {
        setFormState((prev) => ({ ...prev, ...changes }));
    };

    const handleConfirm = async (localOrders: { sale_order: { id: string }; quantity: number }[]) => {
        if (!selectedItem) return;
        const positiveOrders = localOrders.filter((order) => Number(order.quantity || 0) > 0);
        if (positiveOrders.length === 0) {
            error("Lỗi tạo đề nghị nhập kho", "Cần ít nhất một đơn hàng có số lượng lớn hơn 0");
            return;
        }

        const payload = {
            product: selectedItem.product.id,
            receiver_name: formState.receiver_name,
            import_date: formState.import_date.toISOString(),
            import_method: formState.import_method,
            note: formState.note,
            orders: positiveOrders.map((o) => ({
                sale_order: o.sale_order.id,
                quantity: o.quantity,
            })),
        };

        try {
            const result: any = await createBulkImport(payload);
            success(
                "Tạo đề nghị nhập kho thành công",
                `Đã tạo ${result.created_count} đề nghị nhập kho`
            );
            handleCloseModal();
            refetch();
        } catch (err) {
            error("Lỗi tạo đề nghị nhập kho", err);
        }
    };

    const items: PendingProductImport[] = data ?? [];
    const lowerSearch = searchText.toLowerCase();
    const filteredItems = lowerSearch
        ? items.filter(
              (item) =>
                  item.product.name.toLowerCase().includes(lowerSearch) ||
                  item.product.product_code.toLowerCase().includes(lowerSearch),
          )
        : items;

    return (
        <ContentWrapper>
            <TextField
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Tìm theo tên hoặc mã sản phẩm"
                size="small"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#828586" }} />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    width: "320px",
                    marginBottom: "12px",
                    "& .MuiInputBase-root": { height: "36px", fontSize: "13px", fontFamily: "Noto Sans" },
                    "& .MuiInputBase-input": { padding: "8px 12px 8px 0" },
                }}
            />
            {isLoading ? (
                <div style={{ padding: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    Đang tải dữ liệu...
                </div>
            ) : filteredItems.length === 0 ? (
                <div style={{ padding: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    {items.length === 0 ? "Không có sản phẩm nào cần đề nghị nhập kho." : "Không tìm thấy sản phẩm phù hợp."}
                </div>
            ) : (
                <StyledTable stickyHeader>
                    <TableHead>
                        <TableRow>
                            {TABLE_COLUMNS.map((col) => (
                                <StyledTableCell key={col.text} align="left">
                                    {col.text}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.map((item) => (
                            <TableRow key={item.product.id}>
                                <StyledTableCell align="left">{item.product.name}</StyledTableCell>
                                <StyledTableCell align="left">{item.product.product_code}</StyledTableCell>
                                <StyledTableCell align="left">
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", padding: "4px 0" }}>
                                        {item.orders.slice(0, MAX_TAGS_SHOWN).map((o) => (
                                            <Chip
                                                key={o.sale_order.id}
                                                label={o.sale_order.order_name}
                                                size="small"
                                                sx={{
                                                    fontSize: "12px",
                                                    fontFamily: "Noto Sans",
                                                    backgroundColor: "#E4F3FB",
                                                    color: "#2096D9",
                                                    height: "22px",
                                                }}
                                            />
                                        ))}
                                        {item.orders.length > MAX_TAGS_SHOWN && (
                                            <Chip
                                                label={`+${item.orders.length - MAX_TAGS_SHOWN} more`}
                                                size="small"
                                                sx={{
                                                    fontSize: "12px",
                                                    fontFamily: "Noto Sans",
                                                    backgroundColor: "#F2F4FA",
                                                    color: "#828586",
                                                    height: "22px",
                                                }}
                                            />
                                        )}
                                    </div>
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                    {formatNumber(item.total_quantity)}
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                    {canImport ? (
                                        <LoadingButton
                                            variant="contained"
                                            onClick={() => handleOpenModal(item)}
                                            isLoading={isCreating && selectedItem?.product.id === item.product.id}
                                            disabled={isCreating}
                                            text="Đề nghị nhập kho"
                                            style={{
                                                fontSize: "13px",
                                                fontFamily: "Noto Sans",
                                                textTransform: "none",
                                                backgroundColor: "#0058A0",
                                                height: "32px",
                                                borderRadius: "5px",
                                                minWidth: "128px",
                                            }}
                                        />
                                    ) : (
                                        <span style={{ color: "#9A9A9A", fontSize: "13px", fontFamily: "Noto Sans" }}>
                                            —
                                        </span>
                                    )}
                                </StyledTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </StyledTable>
            )}

            <BulkImportModal
                open={isModalOpen}
                item={selectedItem}
                formState={formState}
                isSubmitting={isCreating}
                onClose={handleCloseModal}
                onConfirm={(localOrders) => handleConfirm(localOrders)}
                onFormChange={handleFormChange}
            />
        </ContentWrapper>
    );
};

export default PendingImportContent;
